import {
  Box,
  Typography as Typo,
  Paper,
  useTheme,
  styled,
  Link,
  TypographyProps,
  SxProps,
} from '@mui/material';
import Pro from '@jobseeker/lotus/elements/Pro';
import Tooltip from '@jobseeker/lotus/elements/Tooltip';
import {Button, Switch} from '@jobseeker/lotus/elements';
import {getProLink} from '@jobseeker/lotus/utils';
const Typography = styled(Typo)<TypographyProps>(() => ({
  display: 'flex',
  alignItems: 'center',
}));

type ListPaperProps = {};

const ListPaper = styled(Paper)<ListPaperProps>(({theme}) => ({
  borderRadius: 0,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#fff',
  gap: theme.spacing(1.5),
  '.joblist__logo': {
    borderRadius: theme.spacing(1.5),
    border: '0.6px solid #7E7E7E33',
  },
  '.joblist__right_element': {
    position: 'absolute',
    width: 'fit-content',
    right: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
    padding: theme.spacing(2),
    top: 0,
    bottom: 0,
  },
  '.joblist__subtitle': {
    p: {
      wordBreak: 'break-all',
      color: theme.palette.text.secondary,
    },
  },
  '.save-icon': {
    display: 'none',
  },
  ':hover': {
    '.save-icon': {
      display: 'unset',
    },
  },
}));

type SettingsComponentProps = {
  heading: string;
  description: string | React.ReactNode;
  isPro?: boolean;
  proFeature?: boolean;
  onChange?: (e: any, v: any) => void;
  value?: boolean;
  onClick?: (e: any) => void;
  headingFontSize?: number | string;
  descriptionFontSize?: number | string;
  customBottomMargin?: number | string;
  isLarge?: boolean;
  isLastItem?: boolean;
  mainSx?: SxProps;
};

const SettingsComponent = ({
  heading,
  description,
  isPro = true,
  proFeature,
  headingFontSize,
  descriptionFontSize,
  customBottomMargin,
  isLarge,
  isLastItem,
  mainSx,
  ...props
}: SettingsComponentProps) => {
  const theme = useTheme();

  return (
    <>
      <Box
        color="gray.dark"
        onClick={props.onClick}
        data-testid="notification_list_item"
        className="notification_list_item"
        sx={{
          position: 'relative',
          transition: 'all ease 0.2s',
          cursor: 'pointer',
          borderBottom: 'solid 1px',
          borderColor: 'border.lightGray',
          ...mainSx,
        }}
      >
        <ListPaper
          elevation={0}
          sx={{
            py: {xxs: 1.5, md: 2},
            px: {xxs: 1.5, md: 2},
            ...(isLastItem && {
              mb: {
                xxs: 1.5,
                md: customBottomMargin ?? 1,
              },
            }),
          }}
        >
          <Box
            display={'flex'}
            width={'100%'}
            justifyContent={'space-between'}
            alignItems={'center'}
            gap={3}
          >
            <Box display={'flex'} flexDirection={'column'} width={'75%'}>
              <Typography
                fontWeight={500}
                lineHeight={'20px'}
                component="span"
                fontSize={headingFontSize ?? 14}
                overflow="hidden"
                display={'inline-block'}
                alignItems={'center'}
                color={theme.palette.secondary.main}
                sx={{
                  lineClamp: '3',
                  '-webkit-line-clamp': '2',
                  '-webkit-box-orient': 'vertical',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  wordWrap: 'break-word',
                  alignItems: 'center',
                }}
              >
                {heading}
                {!isPro && proFeature && (
                  <Tooltip
                    disableHoverListener={!isLarge}
                    disableFocusListener={!isLarge}
                    disableTouchListener={!isLarge}
                    placement="bottom-start"
                    title={
                      <>
                        <Box sx={{pr: {md: 12.375}}}>
                          <Typography
                            component={'p'}
                            mb={{xxs: 0.5, md: 0}}
                            variant="subtitle3"
                          >
                            To avail this PRO-Feature
                          </Typography>
                          <Link
                            href={getProLink('ja', '123', '20')}
                            target="_self"
                            sx={{
                              fontSize: '12px',
                              fontWeight: '500',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'none',
                              },
                              '&:focus, &:active': {
                                textDecoration: 'none',
                              },
                            }}
                          >
                            Upgrade to PRO
                          </Link>
                        </Box>
                      </>
                    }
                    arrow
                  >
                    <Box display="block" ml={theme.spacing(0.5)}>
                      <Pro bgColor="linear-gradient(180deg, #FFB743 0%, #F99914 100%)" />
                    </Box>
                  </Tooltip>
                )}
              </Typography>
              <Typography
                fontWeight={400}
                lineHeight={{xxs: '16px', md: '20px'}}
                fontSize={{xxs: 12, md: 14}}
                sx={{
                  color: 'var(--Grey-font-Color, #7E7E7E)',
                  lineClamp: '3',
                  '-webkit-line-clamp': '2',
                  '-webkit-box-orient': 'vertical',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  wordWrap: 'break-word',
                  display: 'block',
                  alignItems: 'center',
                }}
              >
                {description}
              </Typography>
            </Box>
            {(isPro || !proFeature) && (
              <Switch onChange={props.onChange} checked={props.value} />
            )}
          </Box>
        </ListPaper>
      </Box>
    </>
  );
};

export default SettingsComponent;
